import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { MbtiCreateAlertComponent } from "src/app/common/modals/mbti-create-alert/mbti-create-alert.component";
import { AskDetail, AskDetailPaginatedList } from "src/app/interfaces/ask";
import { LoginType } from "src/app/interfaces/log";
import { MBTI } from "src/app/interfaces/mbti";
import { AskService } from 'src/app/services/ask/ask.service';
import { LogService } from 'src/app/services/log/log.service';
import { MbtiService } from 'src/app/services/mbti/mbti.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { RouterService } from 'src/app/services/router/router.service';
import { UserService } from 'src/app/services/user/user.service';
import { getFormattedDate } from "src/utils/utils";
import { AskCreateComponent } from './../../../common/modals/ask/ask-create/ask-create.component';

@Component({
  selector: 'ask-main',
  templateUrl: './ask-main.component.html',
  styleUrls: ['./ask-main.component.scss']
})
export class AskMainComponent implements OnInit, OnDestroy {
  constructor(
    private routerService: RouterService, 
    private modalService: ModalService, 
    private askService: AskService, 
    private mbtiService: MbtiService,
    private logService: LogService,
    private userService: UserService
  ) {

  }

  myMbtiAskList: AskDetail[] = [];
  allAskList: AskDetail[] = [];
  headerTitle = '성격 유형에게 물어보세요';
  private askPostCreateSubscription: Subscription | undefined;
  private askPostDeleteSubscription: Subscription | undefined;
  private userSubscription: Subscription | undefined;

  get myMbti(): MBTI | null | undefined {
    return this.mbtiService.myMbti;
  }
  get isLoggedIn(): boolean {
    return this.userService.isLoggedIn;
  }

  ngOnInit(): void {
    if (this.userSubscription === undefined) {
      this.userSubscription = this.userService.getUserSubject().subscribe(() => {
        this.ngOnInit();
      })
    }
    if (!this.userService.isLoggedIn) {
      this.getAskList();
      return;
    }
    if (this.mbtiService.myMbti === undefined) {
      this.mbtiService.getMyMbtiHostQuizReportList(null).subscribe((res) => {
        if (Array.isArray(res.data)) {
          const [firstData] = res.data;
          this.mbtiService.myMbti = firstData?.mbtiType ?? null;
          this.getAskList(this.mbtiService.myMbti);
        }
        this.subscribeAskPostCreateEvent(this.mbtiService.myMbti ?? undefined);
        this.subscribeAskPostDeleteEvent();
      });
    }
    if (this.mbtiService.myMbti) {
      this.getAskList(this.mbtiService.myMbti);
      this.subscribeAskPostCreateEvent(this.mbtiService.myMbti ?? undefined);
      this.subscribeAskPostDeleteEvent();
    }
  }

  ngOnDestroy(): void {
    if (this.askPostCreateSubscription !== undefined) {
      this.askPostCreateSubscription.unsubscribe();
    }
    if (this.askPostDeleteSubscription !== undefined) {
      this.askPostDeleteSubscription.unsubscribe();
    }
    if (this.userSubscription !== undefined) {
      this.userSubscription.unsubscribe();
    }
  }

  doRefresh(event): void {
    this.ngOnDestroy();
    this.ngOnInit();
  }

  getSectionTitle(mbti?: MBTI): string {
    return mbti ? `${mbti}에게 도착한 질문` : '모든 유형의 질문';
  }
  
  private subscribeAskPostCreateEvent(mbti: MBTI | undefined): void {
    this.askPostCreateSubscription = this.askService.getAskPostCreateSubject().subscribe((data) => {
      if (data.mentionMbtiType === mbti) {
        this.myMbtiAskList.unshift(data);
      }
      this.allAskList.unshift(data);
    })
  }
  
  private subscribeAskPostDeleteEvent(): void {
    this.askPostDeleteSubscription = this.askService.getAskPostDeleteSubject().subscribe((id) => {
      this.myMbtiAskList = this.myMbtiAskList.filter((item) => item.id !== id);
      this.allAskList = this.allAskList.filter((item) => item.id !== id);
    })
  }

  async onClickCreate(): Promise<void> {
    this.homeAskNewPostClickedLogging();
    if (this.userService.navigateToLoginIfAnonymous(LoginType.CLICK_ASK_CREATE)) {
      return;
    }

    if (!this.myMbti) {
      const alertModal = await this.modalService.openBottomSheet(MbtiCreateAlertComponent)
      await alertModal.present();
      return;
    }

    const createModal = await this.modalService.openBottomSheet(AskCreateComponent, {
      from: 'home'
    })
    await createModal.present();
    await createModal.onWillDismiss();
    this.askService.dismissAskCreateModal();
  }

  onClickAsk(post: AskDetail, mbti?: MBTI): void {
    this.homeAskPostItemClickedLogging(post, mbti);
    if (this.userService.navigateToLoginIfAnonymous(LoginType.CLICK_ASK)) {
      return;
    }
    this.navigateToAskDetail(post.id, mbti);
  }

  onClickMore(mbti?: MBTI): void {
    this.homeAskMorePostClickedLogging(mbti);
    if (this.userService.navigateToLoginIfAnonymous(LoginType.CLICK_ASK_MORE)) {
      return;
    }
    this.navigateToAskDetail(undefined, mbti);
  }

  /** main component에서 활용. */
  homeAskImpressionLogging(): void {
    this.logService.ask.homeAskImpression({
      screenName: 'home',
      headerTitle: this.headerTitle,
      userMbti: this.myMbti ?? '',
      postCount: this.getPostCount()
    })
  }
  
  private getAskList(mbtiType?: MBTI): void {
    if (mbtiType !== undefined) {
      this.askService.getAskDetailList(mbtiType)
        .pipe((tap((res: AskDetailPaginatedList) => this.myMbtiAskList = res.data))).subscribe();
    }
    this.askService.getAskDetailList()
      .pipe((tap((res: AskDetailPaginatedList) => this.allAskList = res.data))).subscribe();
  }

  private navigateToAskDetail(id?: number, mbti?: MBTI): void {
    this.routerService.navigate('ask', {queryParams: {mbti, id}});
  }

  private homeAskNewPostClickedLogging(): void {
    this.logService.ask.homeAskNewPostClicked({
      screenName: 'home',
      headerTitle: this.headerTitle,
      userMbti: this.myMbti!,
      postCount: this.getPostCount()
    })
  }

  private homeAskPostItemClickedLogging(post: AskDetail, mbti?: MBTI): void {
    this.logService.ask.homeAskPostItemClicked({
      screenName: 'home',
      headerTitle: this.headerTitle,
      sectionTitle: this.getSectionTitle(mbti),
      userMbti: this.myMbti!,
      postCount: this.getPostCount(),
      summary: post.content,
      commentCount: post.comments.length,
      targetMbti: post.mentionMbtiType === 'ALL' ? undefined : post.mentionMbtiType,
      isTargetMbtiMine: mbti !== undefined,
      createdAt: getFormattedDate(post.createdAt)
    })
  }

  private homeAskMorePostClickedLogging(mbti?: MBTI): void {
    this.logService.ask.homeAskMorePostClicked({
      screenName: 'home',
      headerTitle: this.headerTitle,
      sectionTitle: this.getSectionTitle(mbti),
      userMbti: this.myMbti!,
      postCount: this.getPostCount()
    });
  }

  /** 화면에 노출된 총 포스트 개수 */
  private getPostCount(): number {
    return Math.min(2, this.myMbtiAskList.length) + Math.min(3, this.allAskList.length)
  }
}